import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, Phone } from "lucide-react";
import { BUSINESS } from "@/data/site";

export const usePageMeta = (title, description) => {
  useEffect(() => {
    document.title = title ? `${title} | ${BUSINESS.name}` : `${BUSINESS.name} | Central Florida Stucco Contractor`;
    if (description) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) { tag = document.createElement("meta"); tag.name = "description"; document.head.appendChild(tag); }
      tag.setAttribute("content", description);
    }
    window.scrollTo(0, 0);
  }, [title, description]);
};

export const SectionLabel = ({ children, light }) => (
  <div className={`label-accent flex items-center gap-3 ${light ? "text-adn-red" : "text-adn-red"}`} data-testid="section-label">
    <span className="h-px w-8 bg-adn-red inline-block" />
    {children}
  </div>
);

export const StarRating = ({ rating = 5, size = 18 }) => (
  <div className="flex gap-0.5" data-testid="star-rating">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={size} className={i < rating ? "fill-adn-red text-adn-red" : "text-adn-stone"} />
    ))}
  </div>
);

export const PrimaryButton = ({ to, href, children, testid, className = "", ...rest }) => {
  const cls = `group inline-flex items-center justify-center gap-2 bg-adn-red text-white rounded-none px-8 py-4 font-semibold tracking-wide hover:bg-adn-charcoal transition-colors duration-300 ${className}`;
  const inner = (<>{children}<ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" /></>);
  if (to) return <Link to={to} data-testid={testid} className={cls} {...rest}>{inner}</Link>;
  return <a href={href} data-testid={testid} className={cls} {...rest}>{inner}</a>;
};

export const SecondaryButton = ({ to, href, children, testid, light, className = "" }) => {
  const cls = `inline-flex items-center justify-center gap-2 border-2 rounded-none px-8 py-4 font-semibold tracking-wide transition-colors duration-300 ${light ? "border-white text-white hover:bg-white hover:text-adn-charcoal" : "border-adn-charcoal text-adn-charcoal hover:bg-adn-charcoal hover:text-white"} ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
  return <a href={href} data-testid={testid} className={cls}>{children}</a>;
};

export const TrustMarquee = () => {
  const items = ["Licensed & Insured", "Free Estimates", "Quality Craftsmanship", "Weather-Resistant Finishes", "Honest Pricing", "Clean Job Sites", "Fast Response Times", "Residential & Commercial"];
  const loop = [...items, ...items];
  return (
    <div className="bg-adn-charcoal text-white overflow-hidden py-5 border-y border-white/10" data-testid="trust-marquee">
      <div className="flex marquee-track animate-marquee">
        {loop.map((t, i) => (
          <div key={i} className="flex items-center gap-3 px-8 shrink-0">
            <span className="h-1.5 w-1.5 bg-adn-red rounded-full" />
            <span className="label-accent text-white/90 whitespace-nowrap">{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PageHero = ({ label, title, subtitle, image, testid = "page-hero" }) => (
  <section className="relative bg-adn-charcoal py-28 overflow-hidden" data-testid={testid}>
    {image && <motion.img initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} src={image} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-30" />}
    <div className="absolute inset-0 bg-gradient-to-r from-adn-charcoal via-adn-charcoal/80 to-adn-charcoal/40" />
    <div className="absolute inset-0 noise-overlay opacity-[0.04]" />
    <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="relative max-w-7xl mx-auto px-6">
      {label && <SectionLabel light>{label}</SectionLabel>}
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-white mt-6 max-w-4xl leading-[1.0]">{title}</h1>
      {subtitle && <p className="text-lg text-zinc-300 mt-6 max-w-2xl leading-relaxed">{subtitle}</p>}
    </motion.div>
  </section>
);

export const Breadcrumb = ({ items }) => (
  <div className="bg-adn-off border-b border-zinc-200" data-testid="breadcrumb">
    <div className="max-w-7xl mx-auto px-6 py-3 text-sm text-zinc-500 flex flex-wrap gap-2">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-2">
          {it.to ? <Link to={it.to} className="hover:text-adn-red">{it.label}</Link> : <span className="text-adn-charcoal font-medium">{it.label}</span>}
          {i < items.length - 1 && <span>/</span>}
        </span>
      ))}
    </div>
  </div>
);

export const CTABanner = ({ title = "Ready to Start Your Project?", subtitle = "Get a free, no-obligation estimate from Central Florida's trusted stucco professionals." }) => (
  <section className="relative bg-adn-charcoal text-white py-24 overflow-hidden" data-testid="cta-banner">
    <div className="absolute inset-0 noise-overlay opacity-[0.04]" />
    <div className="relative max-w-5xl mx-auto px-6 text-center">
      <SectionLabel>Free Estimate</SectionLabel>
      <h2 className="text-4xl sm:text-5xl font-heading font-extrabold mt-6 leading-tight">{title}</h2>
      <p className="text-lg text-zinc-300 mt-5 max-w-2xl mx-auto">{subtitle}</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
        <PrimaryButton to="/contact" testid="cta-banner-estimate">Request a Free Estimate</PrimaryButton>
        <a href={BUSINESS.phoneHref} data-testid="cta-banner-call" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white rounded-none px-8 py-4 font-semibold hover:bg-white hover:text-adn-charcoal transition-colors duration-300">
          <Phone size={18} /> {BUSINESS.phone}
        </a>
      </div>
    </div>
  </section>
);
